import React, { useState, useEffect, useContext, useRef } from "react";
import {
    getTeachersOffline,
    getMultipleOfflineCoursePrice,
    getAccountDetails,
    getCounsellorCentre,
    getOfflineCustomerId,
    createOfflineDirectPayment,
    createOfflineExternalPayment,
} from '../../../../../Api/apiUtils';
import { PaymentConfigContext } from '../../../../../Api/global/ApiContext';
import { Button, Field, Loader, Select, Table, TextInput, useConfirm } from "../../../../../ui";
import { FormGrid } from "../ui-pending";
import { toast } from "react-toastify";
import "./style.scss";
import "./payment-panels.scss";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { MixpanelEvents, trackEvent } from '../../../../../utils/mixpanelTracking';

// 1 month of an offline course = 8 classes.
const CLASSES_PER_MONTH = 8;

const FREE_CLASS_REASONS = [
    "Compensatory classes (Due to Academic)",
    "Compensatory classes (Due to Teacher Escalation)",
    "Compensatory classes (Due to Technical Issues)",
    "Compensatory classes (Due to Refund retention)",
    "Compensatory classes (Class deducted due to cancellation)",
    "Compensatory classes (no-show policy)",
    "The learner opted for 30mins classes.",
    "Expiry extension",
    "Operational reasons",
    "Others",
];

const OFFLINE_PAYMENT_MODES = ["Stripe", "PayPal", "Bank transaction", "UPI", "Cash", "POS", "Others"];

// "Guitar - Rock & Pop - Offline" is only sold from Proficient onwards; its lower levels are not offered.
const LEVEL_RESTRICTED_COURSE = "Guitar - Rock & Pop - Offline";
const RESTRICTED_LEVELS = ["Preparatory", "Intermediate"];
const isLevelDisabled = (row: any) =>
    String(row?.courseName || "").trim() === LEVEL_RESTRICTED_COURSE
    && RESTRICTED_LEVELS.includes(String(row?.level || "").trim());

// Offline branches are India only; map branch/centre id -> billing state (mirrors artium-admin-portal).
const getStateForBranch = (branchId: any) => {
    const id = String(branchId);
    if (id === "3") return "Maharashtra";
    if (id === "6") return "Karnataka";
    return "Tamil Nadu";
};

const getAuthToken = () => {
    const cookieMatch = `; ${document.cookie}`.split(`; jwtToken=`);
    const jwtToken = cookieMatch.length === 2 ? (cookieMatch.pop()?.split(';').shift() || "") : "";
    return jwtToken || localStorage.getItem('authToken') || "";
};

/**
 * Offline variant of custom-payment. Used by the offline student profile for both the
 * "Payment Link" tab (razorpay directpayment) and the "Record Payment" tab (isExternal,
 * non-razorpay). Courses come from paymentConfig.offlineCourseCentresV2 (v2Id = paid course id,
 * tag.levelId used to fetch prices); the save mirrors artium-admin-portal's offline flow.
 */
const OfflineCustomPayment: React.FC<any> = ({ studentId, profileData, saveCallBack, isExternal, isFree }) => {
    const paymentConfig = useContext(PaymentConfigContext);
    const offlineCentres = paymentConfig?.offlineCourseCentresV2 || [];

    const [branches, setBranches] = useState<any[]>([]);
    const [selectedBranch, setSelectedBranch] = useState<string>("");
    const [selectedCourse, setSelectedCourse] = useState<string>("");
    const [teachers, setTeachers] = useState<any[]>([]);
    const [selectedTeacher, setSelectedTeacher] = useState<string>("");
    const [teacherLoading, setTeacherLoading] = useState<boolean>(false);

    const [levelRows, setLevelRows] = useState<any[]>([]);   // one row per course level (tag)
    const [currency, setCurrency] = useState<string>("INR");
    const [discount, setDiscount] = useState<string>("0");    // whole-package discount %
    const [enrollmentFee, setEnrollmentFee] = useState<string>("");
    const [enrollmentFeeDiscount, setEnrollmentFeeDiscount] = useState<string>("0");
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [classStartDate, setClassStartDate] = useState<Date | null>(null);

    // record-external-payment (isExternal) fields
    const [refNumber, setRefNumber] = useState<string>("");
    const [paymentMode, setPaymentMode] = useState<string>("Cash");
    const [paymentModeValue, setPaymentModeValue] = useState<string>("Cash");

    // free class (isFree) fields
    const [freeClassReason, setFreeClassReason] = useState<string>("");
    const [freeClassReasonValue, setFreeClassReasonValue] = useState<string>("");

    // sales person shown on the transaction; only an edited name is sent (customSalesAgentName)
    const getLoggedInSalesName = () => localStorage.getItem("loginUser")
        || JSON.parse(localStorage.getItem("userProfileData") || "{}")?.studentName
        || "";
    const [salesAgentNameInput, setSalesAgentNameInput] = useState<string>(getLoggedInSalesName());

    const [priceLoading, setPriceLoading] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const { confirm, dialog } = useConfirm();
    const customerIdRef = useRef<string>("");
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

    // --- Resolve branches based on role (admin => all config branches; offline-admin => assigned only) ---
    useEffect(() => {
        if (!offlineCentres.length) return;
        const allBranches = offlineCentres.map((c: any) => ({
            centerId: c.centerId,
            name: c.locationName || c.centreName || c.name || `Centre ${c.centerId}`,
        }));

        const resolveBranches = async () => {
            const uId = JSON.parse(localStorage.getItem("userProfileData") || "{}")?.uId;
            let resolved = allBranches;
            try {
                if (uId) {
                    const centreData = await getCounsellorCentre(uId);
                    if (Array.isArray(centreData) && centreData.length) {
                        // offline-admin: restrict to assigned centre(s)
                        resolved = centreData.map((c: any) => ({
                            centerId: c.centreId ?? c.centerId,
                            name: c.centreName || c.locationName || c.name || `Centre ${c.centreId ?? c.centerId}`,
                        }));
                    }
                }
            } catch (e) {
                resolved = allBranches;
            }
            setBranches(resolved);
            setSelectedBranch(resolved.length ? String(resolved[0].centerId) : "");
        };
        resolveBranches();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paymentConfig]);

    // fetch a customer id up front (best-effort, mirrors artium-admin-portal)
    useEffect(() => {
        const load = async () => {
            const sId = profileData?.studentId || studentId;
            if (!sId) return;
            const res = await getOfflineCustomerId(sId, "INR");
            customerIdRef.current = res?.data?.customerId || "";
        };
        load();
        return () => {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profileData]);

    // courses available for the selected branch
    const branchCourses = React.useMemo(() => {
        const centre = offlineCentres.find((c: any) => String(c.centerId) === String(selectedBranch));
        return centre?.courses || [];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBranch, paymentConfig]);

    const resetPackage = () => {
        setSelectedCourse("");
        setTeachers([]);
        setSelectedTeacher("");
        setLevelRows([]);
        setDiscount("0");
        setEnrollmentFee("");
        setEnrollmentFeeDiscount("0");
        setFreeClassReason("");
        setFreeClassReasonValue("");
    };

    const handleBranchChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedBranch(e.target.value);
        resetPackage();
    };

    const priceCountry = () => {
        const ccObj = paymentConfig?.timezoneWithCountryName?.[profileData?.timezone];
        return {
            code: ccObj?.code || "+91",
            country: (ccObj?.country || "India").toLowerCase(),
        };
    };

    const handleCourseChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
        const v2Id = e.target.value;
        setSelectedCourse(v2Id);
        setSelectedTeacher("");
        setLevelRows([]);
        setTeachers([]);
        if (!v2Id) return;

        const course = branchCourses.find((c: any) => String(c.v2Id) === String(v2Id));
        if (!course?.tags?.length) {
            toast.error("Course levels not found");
            return;
        }

        // load teachers for this branch + course
        fetchTeachers(v2Id);

        // load level prices + account details
        setPriceLoading(true);
        try {
            const levelIds = course.tags.map((t: any) => Number(t.levelId));
            const { code } = priceCountry();
            // Offline pricing is centre-specific: resolve each level's price from centerPriceMap[branch].
            const priceRes = await getMultipleOfflineCoursePrice(levelIds, code, selectedBranch);
            const priceList = priceRes?.data || [];

            const accountList = await Promise.all(
                course.tags.map(async (t: any) => {
                    try {
                        const acc = await getAccountDetails(t.levelId);
                        return acc?.success ? acc.data : null;
                    } catch (err) {
                        return null;
                    }
                })
            );

            const rowCurrency = "INR";
            const rows = course.tags.map((tag: any, idx: number) => {
                const priceInfo = priceList.find((p: any) => Number(p.levelId) === Number(tag.levelId));
                const account = accountList[idx] || {};
                // Prefer the centre-specific price; fall back to the level's base price.
                const centrePrice = priceInfo
                    ? (priceInfo.centerPriceMap?.[String(selectedBranch)] ?? priceInfo.price)
                    : 0;
                return {
                    levelId: tag.levelId,
                    level: tag.name,
                    courseName: `${course.name}`,
                    unit_price: isFree ? 0 : (centrePrice || 0),
                    months: "0",
                    classCount: 0,
                    account_id: account.account_id || "",
                    item_id: account.item_id || "",
                    item_id_free: account.item_id_free || "",
                    item_id_extra: account.item_id_extra || "",
                    account_id_extra: account.account_id_extra || "",
                };
            });
            setCurrency(rowCurrency);
            setLevelRows(rows);
        } catch (err) {
            console.error("Error loading course pricing:", err);
            toast.error("Unable to load course pricing");
        } finally {
            setPriceLoading(false);
        }
    };

    const fetchTeachers = async (v2Id: string) => {
        setTeacherLoading(true);
        setTeachers([]);
        try {
            const res = await getTeachersOffline(selectedBranch, v2Id);
            const list = res?.content || res?.data?.content || (Array.isArray(res) ? res : []);
            setTeachers(Array.isArray(list) ? list : []);
        } catch (err) {
            console.error("Error fetching offline teachers:", err);
            setTeachers([]);
        } finally {
            setTeacherLoading(false);
        }
    };

    // months -> class count (1 month = 8 classes); Qty (class count) is read only.
    const handleMonthsChange = (levelId: any, value: string) => {
        const sanitized = value.replace(/[^0-9]/g, '');
        setLevelRows(prev => prev.map(r =>
            r.levelId === levelId
                ? { ...r, months: sanitized, classCount: (+sanitized || 0) * CLASSES_PER_MONTH }
                : r
        ));
    };

    const handleDiscountChange = (value: string) => {
        if (!/^\d*\.?\d*$/.test(value)) return;
        setDiscount(value);
    };

    // --- totals ---
    const subtotal = levelRows.reduce((acc, r) => acc + (r.classCount * (+r.unit_price || 0)), 0);
    const discountPct = +discount || 0;
    const discountAmount = +(subtotal * discountPct / 100).toFixed(2);
    const afterDiscount = subtotal - discountAmount;
    const feeAmount = +enrollmentFee || 0;
    const feeDiscountPct = +enrollmentFeeDiscount || 0;
    const enrollmentFeeNet = +(feeAmount - (feeAmount * feeDiscountPct / 100)).toFixed(2);
    const finalAmount = +(afterDiscount + enrollmentFeeNet).toFixed(2);

    const buildCourseList = () => {
        return levelRows
            .filter(r => r.classCount > 0)
            .map(r => {
                const gross = r.classCount * (+r.unit_price || 0);
                const discPrice = +(gross * discountPct / 100).toFixed(2);
                const amountBeforeTax = +(gross / 1.18).toFixed(2);
                return {
                    courseName: r.courseName,
                    teacherId: selectedTeacher || "",
                    courseIndex: "",
                    classesToPurchase: r.classCount,
                    discountedPrice: discPrice,
                    unit_price: r.unit_price,
                    pricePerClass: +(r.unit_price / 1.18).toFixed(2),
                    amountBeforeTax,
                    gst: +(amountBeforeTax * 0.18).toFixed(2),
                    account_id_prod: r.account_id,
                    account_id_uat: r.account_id,
                    item_id_prod: isFree ? (r.item_id_free || r.item_id) : r.item_id,
                    item_id_uat: isFree ? (r.item_id_free || r.item_id) : r.item_id,
                    level: r.level,
                    discountedPercentage: discountPct,
                    courseId: selectedCourse,   // v2Id used as paid course id
                    levelId: r.levelId,
                    trialTeacher: "",
                    price: +(gross - discPrice).toFixed(2),
                    isFree: !!isFree,
                    monthCount: CLASSES_PER_MONTH,
                };
            });
    };

    const buildBasePayload = () => {
        const name = `${(profileData?.firstName || "").trim()}${profileData?.lastName ? ` ${(profileData.lastName || "").trim()}` : ""}`.trim();
        const createdBy = getLoggedInSalesName();
        const editedSalesName = salesAgentNameInput.trim();
        return {
            studentId: profileData?.studentId || studentId,
            batchId: null,
            userId: profileData?.userId,
            totalPrice: +finalAmount.toFixed(2),
            name: name || "No-name",
            contact: profileData?.mobileNumber,
            email: profileData?.emailId,
            countryCode: profileData?.countryCode,
            currency,
            customer_id: customerIdRef.current || "",
            place_of_supply: "",
            token: getAuthToken(),
            artiumCashPoint: 0,
            myCashPoint: 0,
            artiumCash: 0,
            myCash: 0,
            conversionRate: 0,
            referralCode: "",
            version: "2.0",
            excessAmount: false,
            isNonConversionTransaction: false,
            createdBy,
            customSalesAgentName: editedSalesName && editedSalesName !== createdBy ? editedSalesName : "",
            gender: profileData?.gender,
            ageGroup: profileData?.ageGroup,
            studentType: profileData?.studentType,
            userType: profileData?.userType,
            isOffline: true,
            centreId: selectedBranch,
            enrollmentFee: feeAmount ? feeAmount : 0,
            enrollmentFeeDiscount: feeDiscountPct ? feeDiscountPct : 0,
            state: getStateForBranch(selectedBranch),
            courseList: buildCourseList(),
            timeZone: profileData?.timezone || "",
            expiryDate: selectedDate ? selectedDate.toISOString().split('T')[0] : "",
            classStartDate: classStartDate ? classStartDate.toISOString().split('T')[0] : "",
            isFree: !!isFree,
            freeClassReason: isFree ? freeClassReason : "",
        };
    };

    const validate = () => {
        if (!selectedBranch) { toast.error("Please select a branch"); return false; }
        if (!selectedCourse) { toast.error("Please select a course"); return false; }
        // Teacher selection is optional (both paid and free offline flows).
        if (!buildCourseList().length) { toast.error("Please add classes (months) for at least one level"); return false; }
        if (isFree && !freeClassReason.trim()) { toast.error("Please select a reason for free class"); return false; }
        if (!isFree && finalAmount <= 0) { toast.error("Total amount must be greater than zero"); return false; }
        return true;
    };

    const savePaymentLink = async () => {
        if (!validate()) return;
        setLoading(true);
        try {
            const payload = { ...buildBasePayload(), paymentFor: "directpayment" };
            const res = await createOfflineDirectPayment(payload);
            trackEvent(MixpanelEvents.PAYMENT_PACKAGE, {
                type: "Offline Payment Link",
                studentId: payload.studentId,
                courseId: selectedCourse,
                centreId: selectedBranch,
                teacherId: selectedTeacher,
                totalPrice: finalAmount,
            });
            toast.success("Payment link created successfully!");
            resetPackage();
            saveCallBack && saveCallBack(res?.data);
        } catch (err) {
            console.error("Error creating offline payment link:", err);
            toast.error("Failed to create payment link. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const recordExternalPayment = async () => {
        if (!isFree && !paymentMode?.trim()) { toast.error("Please select payment mode"); return; }
        if (!validate()) return;
        const confirmCreate = await confirm({
            title: isFree ? "Are you sure you want to create free class?" : "Are you sure you want to create class?",
            confirmLabel: isFree ? "Create Free Class" : "Create Class",
        });
        if (!confirmCreate) return;
        setLoading(true);
        try {
            const now = new Date();
            const invoice_date = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
            const payload = {
                ...buildBasePayload(),
                paymentFor: "nonrazorpay",
                skipInvoice: false,
                paymentMode,
                refNumber,
                invoice_date,
            };
            const res = await createOfflineExternalPayment(payload);
            trackEvent(MixpanelEvents.PAYMENT_PACKAGE, {
                type: "Offline Record External Payment",
                studentId: payload.studentId,
                courseId: selectedCourse,
                centreId: selectedBranch,
                teacherId: selectedTeacher,
                paymentMode,
                totalPrice: finalAmount,
            });
            if (res?.data?.success) {
                toast.success("Classes added successfully");
                resetPackage();
                saveCallBack && saveCallBack(res?.data);
            } else {
                toast.error(res?.data?.message || "Failed to record payment.");
            }
        } catch (err) {
            console.error("Error recording offline external payment:", err);
            toast.error("Failed to record payment. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const debouncedSave = (fn: () => void) => {
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        if (loading) return;
        debounceTimerRef.current = setTimeout(fn, 600);
    };

    const minPickerDate = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());

    return (
        <div className="ds-stack offline-payment">
            <FormGrid min={240}>
                {/* Branch */}
                <Field label="Branch Name">
                    {(ctl) => (
                        <Select
                            {...ctl}
                            value={selectedBranch}
                            onChange={handleBranchChange}
                            options={branches.map((b: any) => ({ value: String(b.centerId), label: b.name }))}
                        />
                    )}
                </Field>

                {/* Course */}
                <Field label="Select Course">
                    {(ctl) => (
                        <Select
                            {...ctl}
                            value={selectedCourse}
                            onChange={handleCourseChange}
                            disabled={!selectedBranch}
                            options={[
                                { value: "", label: "Select a course" },
                                ...branchCourses.map((course: any) => ({ value: String(course.v2Id), label: course.name })),
                            ]}
                        />
                    )}
                </Field>

                {/* Teacher */}
                <Field label="Select Teacher (optional)">
                    {(ctl) => (
                        <Select
                            {...ctl}
                            value={selectedTeacher}
                            onChange={(e) => setSelectedTeacher(e.target.value)}
                            disabled={!selectedCourse || teacherLoading}
                            options={[
                                { value: "", label: teacherLoading ? "Loading teachers..." : "Select a teacher" },
                                ...teachers.map((t: any) => ({ value: String(t.id), label: t.name })),
                            ]}
                        />
                    )}
                </Field>

                {/* Class Start Date */}
                <Field label="Class Start Date (optional)">
                    {(ctl) => (
                        <DatePicker
                            id={ctl.id}
                            ariaDescribedBy={ctl["aria-describedby"]}
                            selected={classStartDate}
                            onChange={(date: Date | null) => {
                                if (date) {
                                    const adjusted = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12);
                                    setClassStartDate(adjusted);
                                } else {
                                    setClassStartDate(null);
                                }
                            }}
                            minDate={minPickerDate}
                            dateFormat="dd/MM/yyyy"
                            placeholderText="Select Date"
                            className="ds-input"
                            wrapperClassName="date-picker"
                        />
                    )}
                </Field>

                {/* Payment Link Expiry Date */}
                <Field label="Payment Link Expiry Date">
                    {(ctl) => (
                        <DatePicker
                            id={ctl.id}
                            ariaDescribedBy={ctl["aria-describedby"]}
                            selected={selectedDate}
                            onChange={(date: Date | null) => {
                                if (date) {
                                    const adjusted = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12);
                                    setSelectedDate(adjusted);
                                } else {
                                    setSelectedDate(null);
                                }
                            }}
                            minDate={minPickerDate}
                            dateFormat="dd/MM/yyyy"
                            placeholderText="Select Date"
                            className="ds-input"
                            wrapperClassName="date-picker"
                        />
                    )}
                </Field>

                {/* Sales Person Name */}
                {!isFree && (
                    <Field label="Sales person name">
                        {(ctl) => (
                            <TextInput
                                {...ctl}
                                type="text"
                                value={salesAgentNameInput}
                                placeholder="Sales person name"
                                onChange={(e) => setSalesAgentNameInput(e.target.value)}
                            />
                        )}
                    </Field>
                )}
            </FormGrid>

            {priceLoading && <Loader label="Loading course pricing…" />}

            {!priceLoading && selectedCourse && levelRows.length > 0 && (
                <Table caption="Classes by level">
                    <thead>
                        <tr>
                            <th>Level</th>
                            <th className="ds-num">Months</th>
                            <th className="ds-num">Class Count (Qty)</th>
                            <th className="ds-num">Class Price ({currency})</th>
                            <th className="ds-num">Price Detail ({currency})</th>
                        </tr>
                    </thead>
                    <tbody>
                        {levelRows.map((r, i) => (
                            <tr key={i}>
                                {/* Level */}
                                <td className={isLevelDisabled(r) ? "ds-text-subtle" : undefined}>{r.level}</td>
                                {/* Months */}
                                <td className="ds-num">
                                    <TextInput
                                        size="sm"
                                        numeric
                                        className="offline-payment__qty"
                                        aria-label={`Months for ${r.level}`}
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        value={r.months}
                                        onChange={(e) => handleMonthsChange(r.levelId, e.target.value)}
                                        readOnly={isLevelDisabled(r)}
                                        disabled={isLevelDisabled(r)}
                                    />
                                </td>
                                {/* Class Count (read only) */}
                                <td className="ds-num">
                                    <TextInput
                                        size="sm"
                                        numeric
                                        className="offline-payment__qty"
                                        aria-label={`Class count for ${r.level}`}
                                        type="text"
                                        value={r.classCount}
                                        readOnly
                                        disabled
                                    />
                                </td>
                                {/* Class Price */}
                                <td className="ds-num">
                                    <TextInput
                                        size="sm"
                                        numeric
                                        className="offline-payment__qty"
                                        aria-label={`Class price for ${r.level}`}
                                        type="text"
                                        value={r.unit_price}
                                        readOnly
                                        disabled
                                    />
                                </td>
                                {/* Price detail */}
                                <td className="ds-num">
                                    {r.classCount > 0 ? (
                                        <>{r.classCount} * {r.unit_price} = {r.classCount * (+r.unit_price || 0)}</>
                                    ) : null}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colSpan={4}>Total</td>
                            <td className="ds-num">{subtotal.toFixed(2)}</td>
                        </tr>
                        {discountPct > 0 && (
                            <tr>
                                <td colSpan={4}>Discount ({discountPct}%)</td>
                                <td className="ds-num">-{discountAmount.toFixed(2)}</td>
                            </tr>
                        )}
                        {enrollmentFeeNet > 0 && (
                            <tr>
                                <td colSpan={4}>Enrollment Fee</td>
                                <td className="ds-num">{enrollmentFeeNet.toFixed(2)}</td>
                            </tr>
                        )}
                        <tr>
                            <td colSpan={4} className="ds-strong">Final Amount</td>
                            <td className="ds-num ds-strong">{finalAmount.toFixed(2)}</td>
                        </tr>
                    </tfoot>
                </Table>
            )}

            {!isFree && selectedCourse && levelRows.length > 0 && (
                <FormGrid min={180}>
                    <Field label="Discount (%)">
                        {(ctl) => (
                            <TextInput
                                {...ctl}
                                numeric
                                type="text"
                                inputMode="numeric"
                                value={discount}
                                onChange={(e) => handleDiscountChange(e.target.value)}
                            />
                        )}
                    </Field>
                    <Field label={`Enrollment Fee (${currency})`}>
                        {(ctl) => (
                            <TextInput
                                {...ctl}
                                numeric
                                type="text"
                                inputMode="numeric"
                                value={enrollmentFee}
                                onChange={(e) => setEnrollmentFee(e.target.value.replace(/[^0-9]/g, ''))}
                                placeholder="0"
                            />
                        )}
                    </Field>
                    <Field label="Enrollment Fee Discount (%)">
                        {(ctl) => (
                            <TextInput
                                {...ctl}
                                numeric
                                type="text"
                                inputMode="numeric"
                                value={enrollmentFeeDiscount}
                                onChange={(e) => setEnrollmentFeeDiscount(e.target.value.replace(/[^0-9]/g, ''))}
                            />
                        )}
                    </Field>
                </FormGrid>
            )}

            {/* Reason for free class */}
            {isFree && selectedCourse && levelRows.length > 0 && (
                <FormGrid min={320}>
                    <Field label="Reason for free class" required>
                        {(ctl) => (
                            <Select
                                {...ctl}
                                value={freeClassReasonValue}
                                onChange={(e) => {
                                    setFreeClassReasonValue(e.target.value);
                                    setFreeClassReason(e.target.value === "Others" ? "" : e.target.value);
                                }}
                                options={[
                                    { value: "", label: "Please select reason for free class" },
                                    ...FREE_CLASS_REASONS.map((v) => ({ value: v, label: v })),
                                ]}
                            />
                        )}
                    </Field>
                    {freeClassReasonValue === "Others" && (
                        <Field label="Other reason" required>
                            {(ctl) => (
                                <TextInput
                                    {...ctl}
                                    type="text"
                                    value={freeClassReason}
                                    onChange={(e) => setFreeClassReason(e.target.value)}
                                    placeholder="Please enter the reason for free class"
                                />
                            )}
                        </Field>
                    )}
                </FormGrid>
            )}

            {/* Record external payment fields */}
            {isExternal && !isFree && selectedCourse && levelRows.length > 0 && (
                <FormGrid min={240}>
                    <Field label="Payment Reference Number">
                        {(ctl) => (
                            <TextInput
                                {...ctl}
                                type="text"
                                value={refNumber}
                                onChange={(e) => setRefNumber(e.target.value)}
                            />
                        )}
                    </Field>
                    <Field label="Payment Mode" required>
                        {(ctl) => (
                            <Select
                                {...ctl}
                                value={paymentModeValue}
                                onChange={(e) => {
                                    setPaymentModeValue(e.target.value);
                                    setPaymentMode(e.target.value === "Others" ? "" : e.target.value);
                                }}
                                options={OFFLINE_PAYMENT_MODES.map((v) => ({ value: v, label: v }))}
                            />
                        )}
                    </Field>
                    {paymentModeValue === "Others" && (
                        <Field label="Other payment mode" required>
                            {(ctl) => (
                                <TextInput
                                    {...ctl}
                                    type="text"
                                    value={paymentMode}
                                    onChange={(e) => setPaymentMode(e.target.value)}
                                    placeholder="Please enter other payment mode"
                                />
                            )}
                        </Field>
                    )}
                </FormGrid>
            )}

            <div className="package-save-wrapper">
                {isExternal ? (
                    <Button variant="primary" size="lg" onClick={() => debouncedSave(recordExternalPayment)} loading={loading}>
                        {isFree ? "Create Free Class" : "Create Class"}
                    </Button>
                ) : (
                    <Button variant="primary" size="lg" onClick={() => debouncedSave(savePaymentLink)} loading={loading}>
                        Create Payment Link
                    </Button>
                )}
            </div>
            {dialog}
        </div>
    );
};

export default OfflineCustomPayment;
